import { useState } from 'react';
import { FormikHelpers } from 'formik';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useRouter } from 'next/navigation';
import { useLocale } from '@jd/i18n';
import { submitForm } from '@jd/client/form/submit';
import { updateFormSubmission } from '@jd/client/form/updateSubmission';
import { ConditionalRedirect, FormField, FormType } from '../../type';
import { formatSubmissionData } from '../fieldFormatter';
import { splitFiles } from '../files/splitFiles';
import { useFireConversion } from '../tracking/useFireConversion';
import type {
  GoogleAdsTrackingConfig,
  MetaTrackingConfig,
} from '../tracking/types';
import { resolveRedirect } from '../conditions/resolveRedirect';
import { StepGroup } from './useStepGroups';
import { trackEvent } from '../../../../../utils/trackEvent';

type Args = {
  formId?: string;
  allFields: FormField[];
  formType: FormType;
  stepGroups: StepGroup[];
  totalSteps: number;
  withRecaptcha?: boolean;
  redirect?: string;
  metaTracking?: MetaTrackingConfig;
  googleAdsTracking?: GoogleAdsTrackingConfig;
  conditionalRedirects?: ConditionalRedirect[];
};

type StepSubmitState = {
  currentStep: number;
  submissionId?: string;
  error?: string;
  success?: boolean;
  submitting?: boolean;
  redirecting?: boolean;
};

const useMultiStepSubmit = ({
  formId,
  allFields,
  formType,
  stepGroups,
  totalSteps,
  withRecaptcha,
  redirect,
  metaTracking,
  googleAdsTracking,
  conditionalRedirects,
}: Args) => {
  const locale = useLocale();
  const router = useRouter();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [state, setState] = useState<StepSubmitState>({ currentStep: 0 });
  const fireConversion = useFireConversion({
    formId,
    metaTracking,
    googleAdsTracking,
  });

  const getRecaptchaToken = async () => {
    if (!withRecaptcha) return '';
    const token = (await executeRecaptcha?.('dynamic_form')) || '';
    if (!token) throw new Error('Recaptcha failed');
    return token;
  };

  /**
   * Final-step outcome: navigate away when a redirect resolves, otherwise
   * fall back to the inline confirmation message.
   */
  const completeSubmission = (redirectUrl?: string) => {
    if (!redirectUrl) {
      setState((prev) => ({ ...prev, submitting: false, success: true }));
      return;
    }
    setState((prev) => ({ ...prev, redirecting: true }));
    router.push(redirectUrl);
  };

  const goToNextStep = (patch?: Partial<StepSubmitState>) => {
    setState((prev) => ({
      ...prev,
      ...patch,
      currentStep: prev.currentStep + 1,
      submitting: false,
    }));
  };

  const goBack = () => {
    setState((prev) => ({
      ...prev,
      currentStep: Math.max(0, prev.currentStep - 1),
      error: undefined,
    }));
  };

  const onStepSubmit = async (
    values: Record<string, any>,
    bag: FormikHelpers<any>,
  ) => {
    if (!formId) return;

    const isLastStep = state.currentStep === totalSteps - 1;
    const currentGroup = stepGroups[state.currentStep]!;
    const stepFieldNames = new Set(
      currentGroup.fields.map((f) => f.name).filter(Boolean),
    );
    const stepValues = Object.fromEntries(
      Object.entries(values).filter(([key]) => stepFieldNames.has(key)),
    );
    const { values: stepDataValues, files: stepFiles } = splitFiles(
      stepValues,
      currentGroup.fields,
    );
    const stepData = formatSubmissionData(stepDataValues, currentGroup.fields);

    try {
      setState((prev) => ({ ...prev, error: undefined, submitting: true }));

      if (formType === 'multiSaveBetween') {
        await handleSaveBetween({
          values,
          stepData,
          stepFiles,
          isLastStep,
          bag,
        });
      } else {
        await handleSaveAfter({ values, isLastStep, bag });
      }
    } catch {
      setState((prev) => ({
        ...prev,
        submitting: false,
        error: 'Failed to submit form. Please try again.',
      }));
    }
  };

  /**
   * Closing a save-between run: the conversion is tracked before the visitor
   * is landed, a redirect would otherwise cut the browser events short.
   */
  const finishLastStep = async ({
    values,
    bag,
  }: {
    values: Record<string, any>;
    bag: FormikHelpers<any>;
  }) => {
    bag.resetForm();
    const allFormattedValues = formatSubmissionData(
      splitFiles(values, allFields).values,
      allFields,
    );
    await fireConversion(allFormattedValues);
    completeSubmission(
      resolveRedirect(conditionalRedirects, allFormattedValues, redirect),
    );
  };

  const handleSaveBetween = async ({
    values,
    stepData,
    stepFiles,
    isLastStep,
    bag,
  }: {
    values: Record<string, any>;
    stepData: Record<string, any>;
    stepFiles?: FormData;
    isLastStep: boolean;
    bag: FormikHelpers<any>;
  }) => {
    if (!formId) return;
    const isFinalStep = isLastStep;

    if (!state.submissionId) {
      const token = await getRecaptchaToken();
      const result = await submitForm({
        formId,
        recaptchaToken: token,
        data: stepData,
        locale,
        isFinalStep,
        files: stepFiles,
      });
      if (isLastStep) {
        trackEvent('form_submit', {
          form_id: formId,
          event_data: { step: state.currentStep, is_last_step: isLastStep },
        });
        await finishLastStep({ values, bag });
      } else {
        goToNextStep({ submissionId: result.submissionId });
      }
    } else {
      await updateFormSubmission({
        submissionId: state.submissionId,
        formId,
        data: stepData,
        locale,
        isFinalStep,
        files: stepFiles,
      });
      if (isLastStep) {
        await finishLastStep({ values, bag });
      } else {
        goToNextStep();
      }
    }
  };

  const handleSaveAfter = async ({
    values,
    isLastStep,
    bag,
  }: {
    values: Record<string, any>;
    isLastStep: boolean;
    bag: FormikHelpers<any>;
  }) => {
    if (!formId) return;

    if (!isLastStep) {
      goToNextStep();
      return;
    }

    const token = await getRecaptchaToken();
    const { values: dataValues, files } = splitFiles(values, allFields);
    const allData = formatSubmissionData(dataValues, allFields);
    await submitForm({
      formId,
      recaptchaToken: token,
      data: allData,
      locale,
      isFinalStep: true,
      files,
    });

    bag.resetForm();
    await fireConversion(allData);
    trackEvent('form_submit', {
      form_id: formId,
      event_data: { step: state.currentStep, is_last_step: isLastStep },
    });
    trackEvent('generate_lead', {
      form_id: formId,
    });
    completeSubmission(
      resolveRedirect(conditionalRedirects, allData, redirect),
    );
  };

  return {
    currentStep: state.currentStep,
    error: state.error,
    success: state.success,
    isLoading: !!state.submitting || !!state.redirecting,
    goBack,
    onStepSubmit,
  };
};

export default useMultiStepSubmit;
